"use client";

/**
 * Marketing ID Capture and Storage Utility
 * 
 * This utility captures GCLID, MSCLKID, and FBCLID from URL parameters
 * and stores them in first-party cookies for cross-subdomain access.
 */

export interface MarketingIds {
  google_click_id?: string;
  ms_click_id?: string;
  facebook_click_id?: string;
}

export interface MarketingIdConfig {
  urlParam: string;
  cookieName: string;
  hubspotProperty: string;
}

/**
 * Configuration for marketing IDs
 */
export const MARKETING_ID_CONFIG: Record<keyof MarketingIds, MarketingIdConfig> = {
  google_click_id: {
    urlParam: 'gclid',
    cookieName: '_gclid_cap',
    hubspotProperty: 'hs_google_click_id'
  },
  ms_click_id: {
    urlParam: 'msclkid', 
    cookieName: '_msclkid_cap',
    hubspotProperty: 'hs_ms_click_id'
  },
  facebook_click_id: {
    urlParam: 'fbclid',
    cookieName: '_fbclkid_cap', 
    hubspotProperty: 'hs_facebook_click_id'
  }
};

/**
 * Set a cookie with domain scope for cross-subdomain access
 */
export const setCookie = (
  name: string,
  value: string,
  days: number = 90,
  domain?: string
): void => {
  if (typeof window === 'undefined') return;

  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  
  // Use localhost for development, .languex.co for production
  const cookieDomain = domain || (window.location.hostname === 'localhost' ? '' : '.languex.co');
  
  const cookieString = `${name}=${value}; expires=${expires.toUTCString()}; path=/;${cookieDomain ? ` domain=${cookieDomain};` : ''} SameSite=Lax`;
  document.cookie = cookieString;
};

/**
 * Get a cookie value by name
 */
export const getCookie = (name: string): string | null => {
  if (typeof window === 'undefined') return null;

  const nameEQ = name + "=";
  const cookies = document.cookie.split(';');
  
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length);
    }
  }
  
  return null;
};

/**
 * Capture marketing IDs from URL parameters and store in cookies
 * This should be called on page load
 */
export const captureMarketingIds = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const urlParams = new URLSearchParams(window.location.search);
  
  Object.entries(MARKETING_ID_CONFIG).forEach(([key, config]) => {
    const urlValue = urlParams.get(config.urlParam);
    
    if (urlValue) {
      // Store in cookie with 90-day expiration
      setCookie(config.cookieName, urlValue, 90);
      
      // Also store in localStorage for backward compatibility
      localStorage.setItem(config.urlParam, urlValue);
    }
  });
};

/**
 * Get all marketing IDs from cookies
 */
export const getMarketingIds = (): MarketingIds => {
  const ids: MarketingIds = {};
  
  if (typeof window === 'undefined') {
    return ids;
  }
  
  Object.entries(MARKETING_ID_CONFIG).forEach(([key, config]) => {
    const value = getCookie(config.cookieName);
    if (value) {
      ids[key as keyof MarketingIds] = value;
    }
  });
  
  return ids;
};

/**
 * Get a specific marketing ID by key
 */
export const getMarketingId = (key: keyof MarketingIds): string | null => {
  const config = MARKETING_ID_CONFIG[key];
  return getCookie(config.cookieName);
};

/**
 * Clear all marketing ID cookies (useful for testing)
 */
export const clearMarketingIds = (): void => {
  if (typeof window === 'undefined') return;

  Object.values(MARKETING_ID_CONFIG).forEach(config => {
    document.cookie = `${config.cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.languex.co`;
    localStorage.removeItem(config.urlParam);
  });
};

/**
 * Initialize marketing ID capture
 * Call this function early in your app initialization
 */
export const initializeMarketingIds = (): void => {
  // Capture on page load
  captureMarketingIds();
  
  // Listen for popstate changes (browser navigation)
  window.addEventListener('popstate', captureMarketingIds);
  
  // Optional: Listen for pushstate/replacestate (SPA navigation)
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;
  
  history.pushState = function(...args) {
    originalPushState.apply(history, args);
    setTimeout(captureMarketingIds, 0);
  };
  
  history.replaceState = function(...args) {
    originalReplaceState.apply(history, args);
    setTimeout(captureMarketingIds, 0);
  };
};
